import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { trace } from 'owa-trace';
import { orchestrator } from 'satcheljs';
import {
    enableNotificationBar,
    showNotification,
    showNotificationWithButtonCallback,
} from '../actions/notificationBarActions';
import {
    removeNotificationBarCallback,
    setNotificationBarCallback,
    getNotificationBarCallback,
} from '../callbacksMap/notificationBarCallbacks';
import {
    lazyShowNotification,
    NotificationBarCallbackReason as DefaultNotificationBarCallbackReason,
    NotificationBarOptions,
    NotificationBarHostId,
} from 'owa-notification-bar';
import {
    NotificationOptions,
    NotificationButtonOptions,
    NotificationBarCallbackReason,
    CustomNotificationBarCallbackReason,
    DefaultHotKeyCommand,
} from '../schema/NotificationInterfaces';
import { getCommands } from 'owa-calendar-hotkeys';
import { isFeatureEnabled } from 'owa-feature-flags';

const MIN_NOTIFICATION_WIDTH = 300;
const MAX_NOTIFICATION_WIDTH = 600;
const DEFAULT_NOTIFICATION_TIMEOUT_BUTTON = 5;
const DEFAULT_NOTIFICATION_TIMEOUT_NO_BUTTON = 4;
const NOTIFICATION_TIMEOUT_BUTTON_FOR_NO_CONFIRMATION_DIALOG_TEN_SECONDS = 10;
const NOTIFICATION_TIMEOUT_BUTTON_FOR_NO_CONFIRMATION_DIALOG_SEVEN_SECONDS = 7;

let barEnabled = true;

orchestrator(enableNotificationBar, actionMessage => {
    const { enabled } = actionMessage;
    barEnabled = enabled;
});

orchestrator(showNotificationWithButtonCallback, actionMessage => {
    const {
        actionName,
        notificationBarHostId,
        messageString,
        icon,
        primaryActionText,
        primaryActionClickedCallback,
        primaryActionHotKeyCommand,
    } = actionMessage;

    showNotificationInternal(
        actionName,
        notificationBarHostId,
        messageString,
        {
            icon: icon,
            primaryActionHotKeyCommand: primaryActionHotKeyCommand,
        },
        {
            primaryActionText: primaryActionText,
            callbackOptions: {
                primaryActionClickedCallback: primaryActionClickedCallback,
            },
        }
    );
});

orchestrator(showNotification, actionMessage => {
    const {
        actionName,
        notificationBarHostId,
        messageString,
        notificationOptions,
        actionButtonOptions,
    } = actionMessage;

    showNotificationInternal(
        actionName,
        notificationBarHostId,
        messageString,
        notificationOptions,
        actionButtonOptions
    );
});

/**
 * Show a notification with the specified options. The default is to show a notification with the given messageString. Default behavior
 * is to create a notification with no icon at the bottom of the screen that auto dismisses after 5 seconds
 *
 * @param actionName the name of the notification type
 * @param messageString the string that gets shown in the notification
 * @param notificationOptions the options to customize the notification bar itself
 * @param actionButtonOptions the options for the action button in the notification bar, if any
 */
function showNotificationInternal(
    actionName: string,
    notificationBarHostId: NotificationBarHostId,
    messageString: string,
    notificationOptions?: NotificationOptions,
    actionButtonOptions?: NotificationButtonOptions
) {
    const dataPoint = new PerformanceDatapoint('NotificationBarWithForceDismiss');
    dataPoint.addCustomData({ actionName });

    // This is a temporary change to experiment dismiss time with 7 and 10 seconds (only for events deletes)
    // to observe if increase in the dismiss time, reduces the number of accidental deletes.
    const timeoutWithButton =
        actionName === 'instantCalendarEventDelete'
            ? getTimeoutWithButton()
            : DEFAULT_NOTIFICATION_TIMEOUT_BUTTON;
    const hasButton = actionButtonOptions?.primaryActionText;
    const defaultTimeout = hasButton ? timeoutWithButton : DEFAULT_NOTIFICATION_TIMEOUT_NO_BUTTON;

    let defaultOptions = {
        allowAutoDismiss: true,
        shouldShowDismissIcon: true,
        minWidth: MIN_NOTIFICATION_WIDTH,
        maxWidth: MAX_NOTIFICATION_WIDTH,
        autoDismissInSeconds: defaultTimeout,
        primaryActionText: actionButtonOptions?.primaryActionText,
        notificationCallback: createNotificationCallback(
            actionName,
            actionButtonOptions,
            dataPoint
        ),
    };

    let primaryActionHotKeyCommand = null;
    if (notificationOptions.primaryActionHotKeyCommand) {
        if (notificationOptions.primaryActionHotKeyCommand === DefaultHotKeyCommand.Undo) {
            primaryActionHotKeyCommand = getCommands().undoAction;
        } else if (notificationOptions.primaryActionHotKeyCommand === DefaultHotKeyCommand.Retry) {
            primaryActionHotKeyCommand = getCommands().retryAction;
        }
    }

    let notificationBarOptions: NotificationBarOptions = {
        ...defaultOptions,
        ...notificationOptions,
        ...(primaryActionHotKeyCommand && {
            primaryActionHotKeyCommand: primaryActionHotKeyCommand,
        }),
    };

    // components can fire the request to show the notification before they fire the request to re-enable notifications. We want to give the
    // re-enable a chance to run before trying to show, so we delay the check until the end of the current stack of functions
    setTimeout(() => {
        // if the notification bar is explicitly disabled, prevent showing any notifications
        if (!barEnabled) {
            trace.info(
                `Cancelling notification ${actionName} due to barEnabled being false, fire callback immediately`
            );
            notificationBarOptions.notificationCallback?.(
                DefaultNotificationBarCallbackReason.AutoDismissed
            );
            return;
        }

        lazyShowNotification.importAndExecute(
            actionName,
            notificationBarHostId,
            messageString,
            notificationBarOptions
        );
    }, 0);
}

function createNotificationCallback(
    actionName: string,
    actionButtonOptions: NotificationButtonOptions,
    dataPoint: PerformanceDatapoint
): (reason: DefaultNotificationBarCallbackReason) => void {
    let callback = (reason: NotificationBarCallbackReason) => {
        removeNotificationBarCallback();
        dataPoint.addCustomData({
            callbackReason: reason,
        });
        try {
            if (actionButtonOptions) {
                if (actionButtonOptions.notificationCallback) {
                    actionButtonOptions.notificationCallback(reason);
                } else {
                    switch (reason) {
                        case DefaultNotificationBarCallbackReason.PrimaryActionClicked:
                            actionButtonOptions.callbackOptions &&
                                actionButtonOptions.callbackOptions.primaryActionClickedCallback();
                            break;
                        default:
                            actionButtonOptions.callbackOptions?.dismissedCallback?.();
                            break;
                    }
                }
            }
            dataPoint.end(undefined, DatapointStatus.Success);
        } catch (error) {
            dataPoint.endWithError(DatapointStatus.ClientError, Error(error));
            throw error;
        }
    };

    // check if notification for this action name already exists- if so, fire that callback immediately before firing this one
    let existingCallback = getNotificationBarCallback();
    if (existingCallback) {
        existingCallback(CustomNotificationBarCallbackReason.ForceDismissedFromCode);
    }

    setNotificationBarCallback(callback);

    return callback;
}

function getTimeoutWithButton(): number {
    if (isFeatureEnabled('cal-removeDeleteConfirmationWithUndoDurationTenSeconds')) {
        return NOTIFICATION_TIMEOUT_BUTTON_FOR_NO_CONFIRMATION_DIALOG_TEN_SECONDS;
    }

    if (isFeatureEnabled('cal-removeDeleteConfirmationWithUndoDurationSevenSeconds')) {
        return NOTIFICATION_TIMEOUT_BUTTON_FOR_NO_CONFIRMATION_DIALOG_SEVEN_SECONDS;
    }

    return DEFAULT_NOTIFICATION_TIMEOUT_BUTTON;
}
